/**
 * CaseGraphNodes — węzły diagramu sprawy ("Pokaż proces"): węzły kreatora + plakietka
 * położenia sprawy ("Tu jest sprawa" / "Sprawa zakończona").
 *
 * Kreator procesu i diagram sprawy rysują TE SAME klocki (`process-builder/nodes`), żeby
 * sprawa wyglądała dokładnie jak schemat, który narysował autor. Plakietka jest czymś, czego
 * kreator nie zna — dlatego dokładamy ją opakowaniem, a nie zmianą wspólnych węzłów: widok
 * kreatora zostaje bez zmian (nie ustawia `caseMarker`).
 *
 * Plakietka stoi NAD klockiem (`absolute bottom-full`), od lekko na prawo od jego środka
 * (`left-1/2 ml-2`): nie zmienia rozmiaru węzła mierzonego przez React Flow, nie zasłania
 * sąsiedniego klocka (dwa etapy końcowe obok siebie — z boku plakietka lądowałaby na drugim),
 * a strzałka wchodząca (uchwyt na górnym środku) zostaje widoczna. Opakowanie jest `relative`
 * i ma dokładnie rozmiar klocka, więc uchwyty połączeń (`Handle`) zostają w tych samych
 * miejscach co w kreatorze.
 */

import type { ReactElement } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NodeProps, NodeTypes } from '@xyflow/react';
import { Flag, MapPin } from 'lucide-react';

import type { ProcessNodeData } from '@/components/process-builder/graphTypes';
import { processNodeTypes } from '@/components/process-builder/nodes';
import { CASE_MARKER_BADGE_CLASS, type CaseMarker } from '@/lib/processes/step-tone';
import { cn } from '@/lib/utils';

/** Dane węzła diagramu sprawy: dane węzła kreatora + znacznik położenia sprawy. */
export type CaseNodeData = ProcessNodeData & {
  /** `current` = sprawa stoi na tym etapie; `final` = etap końcowy sprawy zakończonej. */
  caseMarker?: CaseMarker;
};

type BaseNodeComponent = NodeTypes[string];

function CaseMarkerBadge({ marker }: { marker: CaseMarker }): ReactElement {
  const { t } = useTranslation();
  const Icon = marker === 'final' ? Flag : MapPin;
  return (
    <span
      data-testid={`case-marker-${marker}`}
      className={cn(
        'pointer-events-none absolute bottom-full left-1/2 mb-2 ml-2 flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold shadow-md',
        CASE_MARKER_BADGE_CLASS[marker],
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {marker === 'final'
        ? t('tenant.processes.flow.finalCase')
        : t('tenant.processes.flow.currentCase')}
    </span>
  );
}

function withCaseMarker(Base: BaseNodeComponent): BaseNodeComponent {
  return memo(function CaseMarkedNode(props: NodeProps) {
    const marker = (props.data as CaseNodeData).caseMarker;
    return (
      <div className="relative">
        <Base {...props} />
        {marker && <CaseMarkerBadge marker={marker} />}
      </div>
    );
  });
}

/**
 * Rejestr typów węzłów diagramu sprawy — te same typy co w kreatorze, każdy z plakietką.
 * Stabilna referencja poza komponentem (React Flow renderuje całość od nowa przy każdej
 * nowej instancji `nodeTypes`).
 */
export const caseNodeTypes: NodeTypes = Object.fromEntries(
  Object.entries(processNodeTypes).map(([type, Base]) => [type, withCaseMarker(Base)]),
);
